import React from 'react'
import gql from 'fake-tag'

import { Button, SpinnerButton, Tooltip, useRouter } from '@forgeglobal/design-framework'
import {
  DealBoardDocument,
  OpportunityDocument,
  OpportunityFragment,
  TradeIntentStatusType,
  useCreateDealButtonMutation,
} from 'graphql/bindings/AdminBindings'
import { Routes } from 'util/Routes'
import { dealBlockersForOpportunity } from 'util/OpportunityUtils'

gql`
  mutation CreateDealButton($tradeIntentId: UUID!) {
    createDealFromTradeIntent(tradeIntentId: $tradeIntentId) {
      id
      ...DealDetailsWrapper
    }
  }
`

export interface CreateDealButtonProps {
  opportunity: OpportunityFragment
}

export const CreateDealButton: React.FC<CreateDealButtonProps> = ({ opportunity }) => {
  const activeDeals = opportunity?.trade_intent?.active_deals
  const { history } = useRouter()

  const [createDealFromTradeIntent, { loading }] = useCreateDealButtonMutation({
    variables: { tradeIntentId: opportunity.id },
    refetchQueries: [{ query: OpportunityDocument, variables: { id: opportunity.id } }, { query: DealBoardDocument }],
  })

  const dealId = activeDeals.length > 0 ? activeDeals[0].id : undefined
  const multipleDeals = activeDeals.length > 1

  const isDealLink = opportunity.status !== TradeIntentStatusType.PrePipeline && dealId
  const dealCreationBlockers = dealBlockersForOpportunity(opportunity)
  const isCreateDealDisabled = !isDealLink && dealCreationBlockers.length > 0

  const createDealTooltipMessage =
    dealCreationBlockers.length > 0 ? dealCreationBlockers.join(', ') : 'Create deal from this opportunity'
  const viewTooltipMessage = multipleDeals
    ? 'There are multiple deals associated with this opportunity'
    : 'View the current deal'

  const handleClick = () => {
    createDealFromTradeIntent().then(result => {
      const newDealId = result.data?.createDealFromTradeIntent.id
      if (newDealId) {
        history.push(Routes.deal(`${newDealId}`))
      }
    })
  }

  return isDealLink ? (
    <Tooltip title={viewTooltipMessage} placement="top" enterDelay={1000}>
      <Button
        size="small"
        onClick={() => {
          if (dealId) history.push(Routes.deal(`${dealId}`))
        }}>
        View Deal
      </Button>
    </Tooltip>
  ) : (
    <Tooltip title={createDealTooltipMessage} placement="top" enterDelay={1000}>
      <div>
        <SpinnerButton
          caption="Create Deal"
          color="primary"
          size="small"
          fullWidth
          disabled={isCreateDealDisabled}
          loading={loading}
          onClick={handleClick}
        />
      </div>
    </Tooltip>
  )
}
